/**
 * IndustryLoginPage — Industry-specific 3-panel login
 * Pulls config from industryConfig.js based on :industryId param
 * Same structure as LoginPage but with industry-specific branding
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, EyeOff, User, Lock, ChevronRight,
  Bell, Activity, Mic, Fingerprint, Scan,
  DollarSign, Users, Package, ShoppingCart, Target, BarChart3,
  Truck, CreditCard, Shield, Settings2,
  Shirt, Gem, Footprints, Hotel, ChefHat, Sofa, Cpu, Smartphone, Store
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGlobalization } from '../context/GlobalizationContext';
import RetailCliqLogo from '../components/RetailCliqLogo';
import ThemeToggle from '../components/ThemeToggle';
import FloatingParticles from '../components/FloatingParticles';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { getIndustryConfig, DEFAULT_INDUSTRY_CONFIG } from '../config/industryConfig';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { API_BASE, API } from '../../lib/api';


// Icon map for industry icons
const ICON_MAP = {
  Shirt, Gem, Footprints, Hotel, ChefHat, Sofa, Cpu, Smartphone, Store,
  DollarSign, Users, Package, ShoppingCart, Target, BarChart3, Truck, CreditCard, Shield, Settings2,
};

const ANN_COLORS = { target: '#3DD56D', feature: '#4CC3FF', compliance: '#F5C451', update: '#A78BFA' };

function useCountUp(target, duration = 1500) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    if (typeof target !== 'number') return;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

export default function IndustryLoginPage({ industryId: propIndustryId }) {
  const params = useParams();
  const resolvedId = propIndustryId || params.industryId || 'retail';
  const config = getIndustryConfig(resolvedId) || DEFAULT_INDUSTRY_CONFIG;

  const { loginWithTemp, completeLogin, accessToken } = useAuth();
  const { isDark } = useTheme();
  const { t, formatTime, formatDate } = useGlobalization();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authTab, setAuthTab] = useState('password');
  const [showForgot, setShowForgot] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [currentAnn, setCurrentAnn] = useState(0);
  const [time, setTime] = useState(new Date());

  const IndustryIcon = ICON_MAP[config.icon] || Store;

  useEffect(() => { if (accessToken) navigate('/select-app', { replace: true }); }, [accessToken, navigate]);

  // Fetch industry-specific announcements
  useEffect(() => {
    axios.get(`${API}/globalization/industries/${resolvedId}/announcements`)
      .then(r => setAnnouncements(r.data.announcements || []))
      .catch(() => setAnnouncements([
        { id: 1, title: config.name + ' Update', body: 'System ready for operations.', type: 'feature', date: 'Today' },
      ]));
  }, [resolvedId, config.name]);

  useEffect(() => {
    if (announcements.length < 2) return;
    const id = setInterval(() => setCurrentAnn(c => (c + 1) % announcements.length), 4000);
    return () => clearInterval(id);
  }, [announcements.length]);

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { toast.error('Please enter credentials'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username: username.trim(), password, remember_me: rememberMe });
      toast.success(`Welcome, ${data.user_summary.name}!`);
      if (data.requiresMfa) { loginWithTemp(data.temp_token, data.user_summary, rememberMe); navigate('/mfa'); }
      else { completeLogin(data.access_token, data.user_summary, rememberMe); navigate('/select-app'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed.');
    } finally { setLoading(false); }
  }, [username, password, rememberMe, loginWithTemp, completeLogin, navigate]);

  const accentColor = config.accent || '#3DD56D';
  const glowBg = config.bgGlow || 'rgba(61,213,109,0.12)';

  return (
    <div className="min-h-screen relative overflow-hidden dark:bg-[#080808] bg-slate-50 pb-10" data-testid={`login-industry-${resolvedId}`}>
      {isDark && <FloatingParticles />}

      {/* Industry color glow overlay */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(700px circle at 15% 50%, ${glowBg}, transparent 55%)` }} />

      <div className="relative z-10 min-h-screen grid grid-cols-12">

        {/* LEFT PANEL — Industry Branding */}
        <div className="hidden lg:flex lg:col-span-3 flex-col p-3 relative overflow-hidden lg:min-h-screen dark:bg-[#0f0f0f] bg-white">
          <div className="absolute top-0 left-0 w-full h-52 pointer-events-none"
            style={{ background: `radial-gradient(380px circle at 0% 0%, ${glowBg}, transparent 60%)` }} />

          <div className="relative z-10 flex flex-col h-full">
            {/* Logo */}
            <div className="mb-3">
              <RetailCliqLogo size="sm" showTagline />
            </div>

            {/* Industry badge */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: accentColor + '20', border: `1px solid ${accentColor}30` }}>
                <IndustryIcon size={16} style={{ color: accentColor }} />
              </div>
              <div>
                <p className="text-[10px] dark:text-neutral-500 text-gray-400 uppercase tracking-widest">Industry</p>
                <p className="text-xs font-semibold dark:text-white text-gray-900">{config.name}</p>
              </div>
            </div>

            {/* Headline */}
            <div className="mb-3">
              <h1 className="text-2xl font-bold dark:text-white text-gray-900 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {config.headline}<br />
                <span style={{ color: accentColor }}>{config.headlineAccent}</span>
              </h1>
              <p className="dark:text-neutral-400 text-gray-500 text-xs mt-1.5 leading-relaxed">{config.description}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-1.5 mb-3">
              {config.stats.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }}
                  className="dark:bg-white/[0.04] bg-white dark:border-white/[0.07] border-gray-100 border rounded-lg p-2">
                  <p className="text-base font-bold tabular-nums" style={{ color: accentColor, fontFamily: "'IBM Plex Mono', monospace" }}>{s.value}</p>
                  <p className="dark:text-neutral-500 text-gray-400 text-[10px] mt-0.5">{s.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Features */}
            <div className="space-y-1.5 flex-1">
              {config.features.map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.06 }}
                  className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: accentColor }} />
                  <span className="dark:text-neutral-300 text-gray-600 text-xs">{f}</span>
                </motion.div>
              ))}
            </div>

            <div className="dark:border-t dark:border-white/[0.06] border-t border-gray-100 pt-2 mt-3">
              <p className="dark:text-neutral-600 text-gray-400 text-[10px]">{config.security}</p>
              <p className="dark:text-neutral-700 text-gray-300 text-[10px] mt-0.5">© 2025 RetailCliQ</p>
            </div>
          </div>
        </div>

        {/* CENTER PANEL — Login Form */}
        <div className="col-span-12 md:col-span-8 lg:col-span-6 flex items-center justify-center p-3 lg:p-5 relative">
          <div className="absolute top-3 right-3 z-20"><ThemeToggle /></div>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.38 }}
            className="w-full max-w-[420px]">
            <div className="lg:hidden flex justify-center mb-4"><RetailCliqLogo size="sm" /></div>

            <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] shadow-lg rounded-2xl p-5">
              <div className="mb-4">
                <h2 className="text-xl font-bold dark:text-white text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {t('welcomeBack')}
                </h2>
                <p className="dark:text-neutral-400 text-gray-500 text-xs mt-1">{config.tagline}</p>
              </div>

              <Tabs value={authTab} onValueChange={setAuthTab} className="mb-1">
                <TabsList className="w-full dark:bg-white/[0.05] bg-gray-100 dark:border-white/[0.08] border-gray-200 border p-0.5 rounded-lg h-auto gap-0.5">
                  <TabsTrigger value="password"
                    className="flex-1 text-[11px] py-1.5 rounded-md dark:data-[state=active]:bg-white/[0.13] data-[state=active]:bg-white dark:data-[state=active]:text-white data-[state=active]:text-gray-900 dark:text-neutral-400 text-gray-500 transition-all">
                    Password
                  </TabsTrigger>
                  {[{ value: 'voice', label: 'Voice' }, { value: 'biometric', label: 'Biometric' }, { value: 'face', label: 'Face ID' }].map(({ value, label }) => (
                    <TabsTrigger key={value} value={value} disabled
                      className="flex-1 text-[11px] py-1.5 rounded-md dark:text-neutral-600 text-gray-300 cursor-not-allowed">{label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="password" className="mt-4" data-dev-component="LoginPortalIndustry">
                  <FormHelp formName="Login">
                  <form onSubmit={handleLogin} className="space-y-3">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={14} />
                      <Input data-testid="login-username-input" data-help-control="username" type="text" placeholder={t('usernamePlaceholder')} value={username}
                        onChange={e => setUsername(e.target.value)} autoComplete="username"
                        className="pl-9 h-9 rounded-lg dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-1 text-sm"
                        style={{ '--tw-ring-color': accentColor }} />
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={14} />
                      <Input data-testid="login-password-input" data-help-control="password" type={showPassword ? 'text' : 'password'} placeholder={t('passwordPlaceholder')}
                        value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
                        className="pl-9 pr-9 h-9 rounded-lg dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-1 text-sm" />
                      <button type="button" onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 hover:text-gray-600 transition-colors">
                        {showPassword ? <Eye size={13} /> : <EyeOff size={13} />}
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Checkbox id="rm-ind" data-help-control="remember_me" checked={rememberMe} onCheckedChange={v => setRememberMe(!!v)}
                          className="h-3.5 w-3.5 border-gray-300 dark:border-white/[0.2]"
                          style={{ '--checkbox-color': accentColor }} />
                        <label htmlFor="rm-ind" className="text-xs dark:text-neutral-400 text-gray-500 cursor-pointer">{t('rememberMe')}</label>
                      </div>
                      <button type="button" onClick={() => setShowForgot(true)}
                        className="text-xs font-medium transition-colors" style={{ color: accentColor }}>{t('forgotPassword')}</button>
                    </div>
                    <Button type="submit" data-testid="login-submit-button" disabled={loading}
                      className="w-full h-9 rounded-lg text-black font-semibold disabled:opacity-50 text-sm transition-colors"
                      style={{ backgroundColor: accentColor, boxShadow: `0 4px 16px ${accentColor}38` }}>
                      {loading
                        ? <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />{t('signingIn')}</span>
                        : <span className="flex items-center gap-1.5">{t('signInBtn')} <ChevronRight size={14} /></span>}
                    </Button>
                  </form>
                  </FormHelp>
                  <div className="mt-3 rounded-lg px-3 py-2 border" style={{ backgroundColor: accentColor + '08', borderColor: accentColor + '20' }}>
                    <p className="text-[11px] dark:text-neutral-400 text-gray-500 font-medium mb-0.5">{t('demoCredentials')}:</p>
                    <p className="text-[11px] dark:text-neutral-300 text-gray-600">admin@retailcliq.com / Admin@123</p>
                    <p className="text-[11px] dark:text-neutral-300 text-gray-600">demo / demo123 {t('noMfaRequired')}</p>
                  </div>
                </TabsContent>

                {['voice', 'biometric', 'face'].map(tab => (
                  <TabsContent key={tab} value={tab} className="mt-4">
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <div className="w-10 h-10 rounded-xl dark:bg-white/[0.05] bg-gray-100 border flex items-center justify-center mb-3" style={{ borderColor: accentColor + '30' }}>
                        {tab === 'voice' && <Mic style={{ color: accentColor }} size={18} />}
                        {tab === 'biometric' && <Fingerprint style={{ color: accentColor }} size={18} />}
                        {tab === 'face' && <Scan style={{ color: accentColor }} size={18} />}
                      </div>
                      <Badge className="mb-2 text-xs" style={{ backgroundColor: accentColor + '18', color: accentColor, borderColor: accentColor + '30' }}>
                        {t('comingSoon')}
                      </Badge>
                      <p className="dark:text-neutral-400 text-gray-500 text-xs max-w-[200px]">Feature coming in next release.</p>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <Separator className="dark:bg-white/[0.07] bg-gray-100 my-3" />
              {/* "Request Access" link hidden per product request
              <p className="text-center text-xs dark:text-neutral-500 text-gray-500">
                {t('newTo')}{' '}
                <a href="/signup" data-testid="login-signup-link" className="font-medium transition-colors" style={{ color: accentColor }}>{t('requestAccess')}</a>
              </p>
              */}
            </div>
          </motion.div>
        </div>

        {/* RIGHT PANEL — Industry Announcements + Activity */}
        <div className="hidden md:flex md:col-span-4 lg:col-span-3 flex-col p-2.5 relative overflow-hidden lg:min-h-screen dark:bg-[#0c0c0c] bg-slate-50">
          <div className="absolute bottom-0 right-0 w-full h-48 pointer-events-none"
            style={{ background: `radial-gradient(280px circle at 110% 110%, ${glowBg}, transparent 60%)` }} />
          <div className="relative z-10 flex flex-col h-full">
            {/* Clock */}
            <div className="mb-3">
              <div className="text-xl font-bold dark:text-white text-gray-900 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{formatTime(time)}</div>
              <div className="dark:text-neutral-500 text-gray-400 text-[10px] mt-0.5">{formatDate(time, { weekday: 'short' })}</div>
            </div>

            {/* Announcements */}
            <div className="mb-3 flex-1">
              <div className="flex items-center gap-1.5 mb-2">
                <Bell size={11} style={{ color: accentColor }} />
                <span className="text-[10px] font-semibold dark:text-neutral-400 text-gray-500 uppercase tracking-[0.12em]">{config.name} News</span>
              </div>
              <div className="relative min-h-[75px]">
                <AnimatePresence mode="wait">
                  {announcements.length > 0 && (
                    <motion.div key={currentAnn} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="dark:bg-white/[0.04] bg-white dark:border-white/[0.07] border-gray-100 border rounded-lg p-2.5">
                      <div className="w-full h-0.5 rounded-full mb-2" style={{ backgroundColor: ANN_COLORS[announcements[currentAnn]?.type] || accentColor }} />
                      <div className="flex items-start justify-between gap-1 mb-1">
                        <p className="dark:text-white text-gray-800 text-[11px] font-semibold leading-snug">{announcements[currentAnn]?.title}</p>
                        <span className="dark:text-neutral-600 text-gray-400 text-[10px] flex-shrink-0">{announcements[currentAnn]?.date}</span>
                      </div>
                      <p className="dark:text-neutral-400 text-gray-500 text-[11px] leading-relaxed">{announcements[currentAnn]?.body}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {announcements.length > 1 && (
                <div className="flex gap-1 justify-center mt-2">
                  {announcements.map((_, i) => (
                    <button key={i} onClick={() => setCurrentAnn(i)}
                      className="h-1 rounded-full transition-all"
                      style={{ width: i === currentAnn ? '16px' : '4px', backgroundColor: i === currentAnn ? accentColor : 'rgba(255,255,255,0.15)' }} />
                  ))}
                </div>
              )}

              {/* Activity feed */}
              <div className="mt-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Activity size={11} className="dark:text-neutral-500 text-gray-400" />
                  <span className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-[0.12em]">{t('recentActivity')}</span>
                </div>
                <div className="space-y-2">
                  {[
                    { action: 'Sales Report Generated', time: 'Today', mod: config.name },
                    { action: 'Inventory Updated', time: 'Yesterday', mod: 'Stock' },
                    { action: 'New Order Processed', time: 'Yesterday', mod: 'POS' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: accentColor + '80' }} />
                      <div className="min-w-0">
                        <p className="dark:text-neutral-300 text-gray-600 text-[11px] truncate">{item.action}</p>
                        <p className="dark:text-neutral-600 text-gray-400 text-[10px]">{item.time} · {item.mod}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="dark:border-t dark:border-white/[0.06] border-t border-gray-100 pt-2">
              <p className="dark:text-neutral-600 text-gray-400 text-[10px] italic">"{config.quote}"</p>
            </div>
          </div>
        </div>

      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}
